import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import SVG from 'react-inlinesvg';

import Avatar from '@common/components/avatar';
import Input from '@common/components/input';
import svgMagGlassIcon from '@common/assets/svg/icon-mag_glassicon.svg';
import svgLoadingIcon from '@common/assets/svg/icon-loading_spinner.svg';
import svgAvatar from '@common/assets/svg/icon-avatar.svg';
import { TNullableString, TUnknownObject } from '@common/types/common';
import { buildFullName } from '@common/utils/common';
import { VARIATION_ROUND } from '@common/components/input/input';

import providerApi from '@utils/api';
import userInterfaceStore from '@store/user-interface';
import { TRootState } from '@store/root-reducer';

import SearchSC, { ExpertRowSC } from './search.styled';
import HearthBeatLoader from '@common/components/loaders/heart-beat/hb-loader';

type TExpertRowProps = {
  name: string;
  expertise: string;
  profileImage: TNullableString;
  onSelect: () => void;
  className?: string;
};

const ExpertRow: React.FC<TExpertRowProps> = (props: TExpertRowProps) => {
  const { name, expertise, profileImage, onSelect, className } = props;
  return (
    <ExpertRowSC className={className} onClick={() => onSelect()}>
      <div className="left">
        <Avatar imgUrl={profileImage ?? undefined} />
        <div className="text">
          <h5>{name}</h5>
          <span>{expertise}</span>
        </div>
      </div>
    </ExpertRowSC>
  );
};

const Search: React.FC = () => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [phrase, setPhrase] = useState('');

  const selectedExpertId = useSelector(
    (store: TRootState) => store.userInterface.order.expert?.id,
  );

  const { isLoading, error, data } = useQuery(['experts', phrase], async () => {
    const result = await providerApi.custom('/expert/search', {
      method: 'POST',
      data: {
        query: phrase,
        pagination: {
          page: 1,
          pageSize: 50,
          sortBy: 'lastName',
          direction: 'ASC',
        },
      },
    });
    return result.data;
  });

  const handleSelectExpert = (id: string, name: string) => {
    dispatch(userInterfaceStore.selectExpert({ id, name }));
    dispatch(userInterfaceStore.moveToNextStep());
  };

  const handlePhraseChange = (e: React.FormEvent<HTMLInputElement>) => {
    setPhrase(e.currentTarget.value);
  };

  return (
    <SearchSC>
      <div className={'header'}>
        <h4>{formatMessage({ id: 'search_expert.title' })}</h4>
        <Input
          variation={VARIATION_ROUND}
          className="search-input"
          value={phrase}
          onChange={handlePhraseChange}
          placeholder="Začnite písať"
          ref={inputRef}
          suffix={<SVG src={isLoading ? svgLoadingIcon : svgMagGlassIcon} />}
        />
        {isLoading && (
          <div className="loading">
            <HearthBeatLoader />
          </div>
        )}
      </div>
      <div className={'content'}>
        {!isLoading && (
          <div className="experts-list">
            {data &&
              Array.isArray(data.items) &&
              data.items.length > 0 &&
              data.items.map((exp: TUnknownObject, idx: number) => {
                const {
                  firstName,
                  lastName,
                  titleBeforeName,
                  titleAfterName,
                  profileImage,
                } = exp.profile;

                const fullName = buildFullName(
                  titleBeforeName,
                  firstName,
                  lastName,
                  titleAfterName,
                );

                // resolve expertise
                const expertise = exp.expertises.filter(
                  (exp: TUnknownObject) => exp.type === 'primary',
                )[0]?.name;

                return (
                  <ExpertRow
                    key={idx}
                    name={fullName}
                    className={exp.id === selectedExpertId ? 'active' : ''}
                    expertise={expertise}
                    profileImage={profileImage}
                    onSelect={() => handleSelectExpert(exp.id, fullName)}
                  />
                );
              })}
            {(!data ||
              error ||
              (Array.isArray(data.doctors) && data.doctors.length <= 0)) && (
              <div className="no-experts">
                <span>Nenašli sa žiadni lekári</span>
                <SVG src={svgAvatar} />
              </div>
            )}
          </div>
        )}
      </div>
    </SearchSC>
  );
};

export default Search;
